
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Role } from '@/models/Role';
import { RolePermissionManager } from './RolePermissionManager';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

interface RoleEditDialogProps {
  role?: Role;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (values: { name: string }) => void;
}

export function RoleEditDialog({ role, open, onOpenChange, onSave }: RoleEditDialogProps) {
  const form = useForm({
    defaultValues: {
      name: "",
    }
  });

  // Reset form when role changes
  useEffect(() => {
    if (role) {
      form.reset({
        name: role.name
      });
    } else {
      form.reset({
        name: ""
      });
    }
  }, [role, form]);

  const handleSubmit = async (values: { name: string }) => {
    // First, save the role details
    onSave(values);
    
    // Then if we're editing a role (not creating a new one),
    // also save the permissions
    if (role) {
      try {
        // Use the globally available function to save permissions
        // This is a workaround for direct communication between components
        // @ts-ignore
        const savePermissions = window[`saveRolePermissions_${role.id}`];
        if (typeof savePermissions === 'function') {
          await savePermissions();
        }
      } catch (error) {
        console.error('Failed to save permissions:', error);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{role ? 'Edit Role' : 'Add Role'}</DialogTitle>
          <DialogDescription>
            {role ? 'Modify role details and permissions.' : 'Create a new role and assign permissions.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter role name" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            {role && (
              <RolePermissionManager
                roleId={role.id}
              />
            )}

            <DialogFooter>
              <Button type="submit">
                {role ? 'Save Changes' : 'Create Role'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
